import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { IoMdArrowDropdown } from 'react-icons/io';
import TagSelecionada from './TagSelecionada';

/**
 * GRI-06 — CA2.3: seleção das empresas de um grupo de comparação.
 *
 * Mesma mecânica do GriMultiSelect: Popover em vez de Menu, porque o Menu do
 * Chakra tem typeahead e rouba o foco do Input a cada tecla.
 *
 * SEM Portal, e isto é o conserto de um bug: este campo vive DENTRO de um
 * Modal, e o Modal do Chakra prende o foco no próprio conteúdo. Painel
 * portalado para fora fica fora dessa trava — abria e não dava para escolher
 * empresa nenhuma. `strategy='fixed'` sozinho já tira o painel do contexto de
 * rolagem, que era a única razão do Portal. (No FilterCombobox, que vive numa
 * tabela e não num modal, o Portal continua fazendo sentido.)
 *
 * A diferença que justifica um componente próprio é a informação na linha: cada
 * empresa mostra o grupo em que JÁ está, que é o que transforma o CA3.1 numa
 * decisão informada em vez de um susto no salvamento.
 *
 * A lista vem pronta de quem chama (`/comparison-group/companies` já devolve a
 * anotação), então este componente não busca nada — o modal precisa da mesma
 * lista para montar a confirmação de movimentação, e duas buscas divergiriam.
 */
const CompanyMultiSelect = ({
  companies = [],
  value = [],
  onChange,
  currentGroupId = null,
  isDisabled = false,
  isLoading = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [busca, setBusca] = useState('');
  const buscaRef = useRef(null);

  const selecionados = useMemo(
    () => (value || []).map(Number).filter((id) => !Number.isNaN(id)),
    [value]
  );

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return companies;

    return companies.filter(
      (empresa) =>
        empresa.name?.toLowerCase().includes(termo) ||
        empresa.fantasyname?.toLowerCase().includes(termo)
    );
  }, [companies, busca]);

  const escolhidas = useMemo(
    () =>
      selecionados.map(
        (id) =>
          companies.find((empresa) => empresa.id_company === id) || {
            id_company: id,
            name: `#${id}`,
          }
      ),
    [selecionados, companies]
  );

  /** Acrescenta as empresas informadas às já escolhidas, sem duplicar. */
  const marcarTodas = (empresas) => {
    const novas = empresas.map((empresa) => Number(empresa.id_company));
    onChange([...new Set([...selecionados, ...novas])]);
  };

  const alternar = (id) => {
    const proximo = selecionados.includes(id)
      ? selecionados.filter((atual) => atual !== id)
      : [...selecionados, id];

    onChange(proximo);
  };

  const fechar = () => {
    setBusca('');
    onClose();
  };

  /** Grupo de OUTRA pessoa: o próprio grupo em edição não é "movimentação". */
  const grupoDeOutro = (empresa) =>
    empresa.comparison_group_name &&
    String(empresa.id_comparison_group) !== String(currentGroupId)
      ? empresa.comparison_group_name
      : null;

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={fechar}
        initialFocusRef={buscaRef}
        placement='bottom-start'
        matchWidth
        isLazy
        strategy='fixed'
      >
        <PopoverTrigger>
          <Button
            w='100%'
            h='40px'
            px='12px'
            bg='white'
            border='1px solid'
            borderColor='gray.200'
            borderRadius='8px'
            fontSize='sm'
            fontWeight={500}
            color={escolhidas.length ? 'black' : 'gray.500'}
            _hover={{ bg: 'white' }}
            _active={{ bg: 'white' }}
            isLoading={isLoading}
            isDisabled={isDisabled}
            rightIcon={<IoMdArrowDropdown fontSize='22px' opacity={0.5} />}
          >
            <Text flex='1' textAlign='left' noOfLines={1}>
              {escolhidas.length
                ? `${escolhidas.length} empresa${
                    escolhidas.length > 1 ? 's' : ''
                  } selecionada${escolhidas.length > 1 ? 's' : ''}`
                : 'Selecione as empresas'}
            </Text>
          </Button>
        </PopoverTrigger>

        <PopoverContent borderRadius='8px' _focusVisible={{ outline: 'none' }}>
          <PopoverBody p='8px'>
              <Input
                ref={buscaRef}
                size='sm'
                borderRadius='6px'
                placeholder='Buscar empresa'
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                mb='8px'
              />

              {/* Marcar tudo age sobre o que está FILTRADO: com uma busca
                  ativa, "todas" que ignorasse o filtro traria empresas que o
                  usuário não está vendo na lista. */}
              <Flex justify='space-between' align='center' mb='6px' px='2px'>
                <Box
                  as='button'
                  type='button'
                  fontSize='11px'
                  fontWeight={600}
                  color='#0B4A8F'
                  onClick={() => marcarTodas(filtradas)}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {busca.trim() ? 'Marcar as filtradas' : 'Marcar todas'}
                </Box>
                <Box
                  as='button'
                  type='button'
                  fontSize='11px'
                  fontWeight={600}
                  color='#8A94A6'
                  onClick={() => onChange([])}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Limpar
                </Box>
              </Flex>

              <Box maxH='260px' overflowY='auto'>
                {filtradas.map((empresa) => {
                  const outroGrupo = grupoDeOutro(empresa);

                  return (
                    <Box
                      key={empresa.id_company}
                      px='8px'
                      py='6px'
                      borderRadius='6px'
                      _hover={{ bg: '#F4F6F8' }}
                    >
                      <Checkbox
                        isChecked={selecionados.includes(empresa.id_company)}
                        onChange={() => alternar(empresa.id_company)}
                        alignItems='flex-start'
                      >
                        <Text fontSize='sm' lineHeight='1.3'>
                          {empresa.name}
                        </Text>
                        {/* CA3.1 na origem: o gestor vê de onde a empresa sai
                            ANTES de escolher, não depois de tentar salvar. */}
                        {outroGrupo && (
                          <Text fontSize='xs' color='#B7791F'>
                            já no grupo {outroGrupo}
                          </Text>
                        )}
                      </Checkbox>
                    </Box>
                  );
                })}

                {filtradas.length === 0 && (
                  <Text fontSize='sm' color='#818A8F' px='8px' py='12px'>
                    Nenhuma empresa encontrada
                  </Text>
                )}
              </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      {escolhidas.length > 0 && (
        <Flex gap='6px' wrap='wrap' mt='8px'>
          {escolhidas.map((empresa) => (
            <TagSelecionada
              key={empresa.id_company}
              label={empresa.name}
              onRemove={() => alternar(empresa.id_company)}
              isDisabled={isDisabled}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default CompanyMultiSelect;
